import {
  HttpContext,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { catchError, throwError } from 'rxjs';
import { environment } from '@env/environment';
import { BaseDeRefService } from '@shared/services/cabinet/basederef.service';
import { SiretService } from '@shared/services/siret/siret.service';
import { Deconnexion } from '../session';
import { REFUS_DOSSIER_GERE, VERIFICATION_SESSION } from '@shared/interceptors/contexte-http';

/** Appel de l'API principale hors connexion (Login/…) : un 401 sans jeton y signifie une session perdue. */
export function appelDeSession(url: string, systeme = environment.apiUrl.system): boolean {
  return !url.startsWith(`${systeme}Login/`);
}

/**
 * 401 traité par l'appelant, sans déconnexion : ouverture d'un dossier (refus possible du dossier, l'appelant vérifie
 * la session) ou vérification d'une ancienne session sur l'écran de connexion (session expirée, oubliée sans message).
 */
export function refusGereParAppelant(contexte: HttpContext): boolean {
  return contexte.get(REFUS_DOSSIER_GERE) || contexte.get(VERIFICATION_SESSION);
}

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
) => {
  const deconnexion = inject(Deconnexion);

  let token;
  // Appel des services annexes (base de référence, Siret) : leur 401 ne concerne pas la session.
  let annexe = true;
  if (req.url.startsWith(environment.apiUrl.baseref)) {
    token = inject(BaseDeRefService).getTokenFromLocal();
  } else if (req.url.startsWith(environment.apiUrl.siret)) {
    token = inject(SiretService).getTokenFromLocal();
  } else {
    token = inject(AuthService).getAuthToken();
    annexe = false;
  }

  let newReq = req;
  if (token) {
    let headers = req.headers;
    headers = headers.append('Authorization', `Bearer ${token}`);
    headers = headers.append('X-WEB', '1');
    newReq = req.clone({
      headers,
    });
  }
  return next(newReq).pipe(
    catchError((err) => {
      if (err.status === 401 && !refusGereParAppelant(req.context)) {
        if (token) {
          // Jeton refusé (session expirée côté back). Plusieurs 401 en même temps : un seul message, un seul retour
          // à la connexion (Deconnexion.enCours).
          deconnexion.sessionPerdue(true);
        } else if (!annexe && appelDeSession(req.url)) {
          // Appel parti sans jeton : jeton retiré par une déconnexion dans un autre onglet.
          deconnexion.sessionPerdue();
        }
      }
      return throwError(() => err);
    }),
  );
};
